import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { fetchFlightPairingStatus, requestFlightPairingStatusRecompute } from "src/API/FlightPairingStatusV2";
import { dayKeyRange } from "src/pages/FlightPairingStatusV2/pairingStatusSelectors";
import {
  IEpochRange,
  IFlightPairingStatusMeta,
  IFlightPairingStatusRecomputeResponse,
  IFlightPairingStatusRow,
  IFlightPairingStatusWebsocketUpdate,
} from "src/pages/FlightPairingStatusV2/pairingStatusTypes";
import { IRootState } from "src/redux/reducers/CombinedReducers";

export enum Type {
  FPS_LOAD_STARTED = "FPS_V2_LOAD_STARTED",
  FPS_LOAD_SUCCEEDED = "FPS_V2_LOAD_SUCCEEDED",
  FPS_LOAD_FAILED = "FPS_V2_LOAD_FAILED",
  FPS_RECOMPUTE_STARTED = "FPS_V2_RECOMPUTE_STARTED",
  FPS_RECOMPUTE_FINISHED = "FPS_V2_RECOMPUTE_FINISHED",
  FPS_ROWS_UPSERTED = "FPS_V2_ROWS_UPSERTED",
  FPS_ROWS_REMOVED = "FPS_V2_ROWS_REMOVED",
  FPS_ROWS_VERIFIED = "FPS_V2_ROWS_VERIFIED",
  FPS_FLIGHT_SELECTED = "FPS_V2_FLIGHT_SELECTED",
  FPS_WINDOW_OPENED = "FPS_V2_WINDOW_OPENED",
  FPS_WINDOW_CLOSED = "FPS_V2_WINDOW_CLOSED",
}

export interface ILoadStarted {
  type: Type.FPS_LOAD_STARTED;
  range: IEpochRange;
  requestId: number;
  silent: boolean;
}
export interface ILoadSucceeded {
  type: Type.FPS_LOAD_SUCCEEDED;
  range: IEpochRange;
  requestId: number;
  rows: IFlightPairingStatusRow[];
  meta: IFlightPairingStatusMeta | null;
  loadedAt: number;
}
export interface ILoadFailed {
  type: Type.FPS_LOAD_FAILED;
  requestId: number;
  error: string;
}
export interface IRecomputeStarted {
  type: Type.FPS_RECOMPUTE_STARTED;
}
export interface IRecomputeFinished {
  type: Type.FPS_RECOMPUTE_FINISHED;
}
export interface IRowsUpserted {
  type: Type.FPS_ROWS_UPSERTED;
  rows: IFlightPairingStatusRow[];
  updatedAt: number;
}
export interface IRowsRemoved {
  type: Type.FPS_ROWS_REMOVED;
  flightIds: string[];
  updatedAt: number;
}
/** A scheduled refresh evaluated every flight filed under the UTC days [dayKeyStart, dayKeyEnd] at verifiedAt. */
export interface IRowsVerified {
  type: Type.FPS_ROWS_VERIFIED;
  /** First verified UTC day, YYYY-MM-DD (inclusive). */
  dayKeyStart: string;
  /** Last verified UTC day, YYYY-MM-DD (inclusive). */
  dayKeyEnd: string;
  /** Epoch ms the evaluation started. */
  verifiedAt: number;
  /** The refresh's window as sent (the days it keeps fresh), when known. */
  window?: IEpochRange;
}
export interface IFlightSelected {
  type: Type.FPS_FLIGHT_SELECTED;
  flightId: string | null;
}
export interface IWindowOpened {
  type: Type.FPS_WINDOW_OPENED;
}
export interface IWindowClosed {
  type: Type.FPS_WINDOW_CLOSED;
}

export type Action =
  | ILoadStarted
  | ILoadSucceeded
  | ILoadFailed
  | IRecomputeStarted
  | IRecomputeFinished
  | IRowsUpserted
  | IRowsRemoved
  | IRowsVerified
  | IFlightSelected
  | IWindowOpened
  | IWindowClosed;

type FpsThunk<R = void> = ThunkAction<R, IRootState, object, AnyAction>;
type FpsDispatch = ThunkDispatch<IRootState, object, AnyAction>;

let requestCounter = 0;
/** computedAt of the most recent manual recompute this client requested (its websocket echo needs no reload). */
let lastManualComputedAt: number | null = null;
/**
 * Counts live (websocket) changes applied to the rows. A load replaces every row with what it fetched, so a change
 * applied while its pages were still loading would be lost; the load compares this count and reloads if it moved.
 */
let liveChanges = 0;

export const loadStarted = (range: IEpochRange, requestId: number, silent: boolean): ILoadStarted => ({
  type: Type.FPS_LOAD_STARTED,
  range,
  requestId,
  silent,
});
export const loadSucceeded = (
  range: IEpochRange,
  requestId: number,
  rows: IFlightPairingStatusRow[],
  meta: IFlightPairingStatusMeta | null,
  loadedAt: number = Date.now()
): ILoadSucceeded => ({ type: Type.FPS_LOAD_SUCCEEDED, range, requestId, rows, meta, loadedAt });
export const loadFailed = (requestId: number, error: string): ILoadFailed => ({
  type: Type.FPS_LOAD_FAILED,
  requestId,
  error,
});
export const recomputeStarted = (): IRecomputeStarted => ({ type: Type.FPS_RECOMPUTE_STARTED });
export const recomputeFinished = (): IRecomputeFinished => ({ type: Type.FPS_RECOMPUTE_FINISHED });
export const rowsUpserted = (rows: IFlightPairingStatusRow[], updatedAt: number = Date.now()): IRowsUpserted => ({
  type: Type.FPS_ROWS_UPSERTED,
  rows,
  updatedAt,
});
export const rowsRemoved = (flightIds: string[], updatedAt: number = Date.now()): IRowsRemoved => ({
  type: Type.FPS_ROWS_REMOVED,
  flightIds,
  updatedAt,
});
export const rowsVerified = (
  dayKeyStart: string,
  dayKeyEnd: string,
  verifiedAt: number,
  window?: IEpochRange
): IRowsVerified => ({
  type: Type.FPS_ROWS_VERIFIED,
  dayKeyStart,
  dayKeyEnd,
  verifiedAt,
  window,
});
export const selectFlightPairingStatus = (flightId: string | null): IFlightSelected => ({
  type: Type.FPS_FLIGHT_SELECTED,
  flightId,
});
export const flightPairingStatusWindowOpened = (): IWindowOpened => ({ type: Type.FPS_WINDOW_OPENED });
export const flightPairingStatusWindowClosed = (): IWindowClosed => ({ type: Type.FPS_WINDOW_CLOSED });

/**
 * Load the cached status for a range. Responses for superseded requests (the range changed while a load was
 * in flight) are ignored by the reducer via `requestId`.
 * @param silent - keep the current rows on screen and do not show the loading state (used for live refreshes)
 */
export const loadFlightPairingStatus =
  (range: IEpochRange, silent = false): FpsThunk<Promise<void>> =>
  async (dispatch: FpsDispatch): Promise<void> => {
    const requestId = ++requestCounter;
    const changesBefore = liveChanges;
    dispatch(loadStarted(range, requestId, silent));
    const result = await fetchFlightPairingStatus(range, dispatch);
    if (!result) {
      dispatch(loadFailed(requestId, "Unable to load flight pairing status"));
      return;
    }
    dispatch(loadSucceeded(range, requestId, result.rows, result.meta));
    // Live changes arrived while the pages loaded and the response may predate them: fetch again.
    if (liveChanges !== changesBefore) dispatch(reloadFlightPairingStatusSoon());
  };

const rangesOverlap = (a: IEpochRange, b: IEpochRange): boolean => a.start <= b.end && b.start <= a.end;

/** Rows are selected by the UTC day they are shown in, so range membership is decided the same way. */
const isInRange = (row: IFlightPairingStatusRow, range: IEpochRange): boolean => {
  const keys = dayKeyRange(range);
  return row.dayKey >= keys.start && row.dayKey <= keys.end;
};

let pendingReload: ReturnType<typeof setTimeout> | null = null;
const RELOAD_DEBOUNCE_MS = 1500;

/**
 * Silently reload the range the window is (or is about to be) showing. The requested range is used rather
 * than the loaded one so a reload can never resurrect a range the user has already navigated away from.
 */
export const reloadFlightPairingStatus =
  (): FpsThunk<Promise<void>> =>
  async (dispatch: FpsDispatch, getState: () => IRootState): Promise<void> => {
    const state = getState().flightPairingStatusV2;
    const range = state.requestedRange ?? state.range;
    if (!range || state.openWindows === 0) return;
    await dispatch(loadFlightPairingStatus(range, true));
  };

/**
 * Reload soon, coalescing bursts of websocket updates into one request.
 */
export const reloadFlightPairingStatusSoon =
  (): FpsThunk =>
  (dispatch: FpsDispatch): void => {
    if (pendingReload) clearTimeout(pendingReload);
    pendingReload = setTimeout(() => {
      pendingReload = null;
      void dispatch(reloadFlightPairingStatus());
    }, RELOAD_DEBOUNCE_MS);
  };

/**
 * Apply the changes a websocket cache update carries: documents are merged in place, removals are dropped, and
 * anything larger is turned into a (debounced) reload of the range in use.
 */
const applyCacheChanges = (
  dispatch: FpsDispatch,
  rows: Record<string, IFlightPairingStatusRow>,
  range: IEpochRange,
  update: IFlightPairingStatusWebsocketUpdate
): void => {
  const updatedAt = update.computedAt ?? Date.now();

  if (update.removedFlightIds?.length) {
    const known = update.removedFlightIds.filter((id) => !!rows[id]);
    if (known.length) {
      liveChanges++;
      dispatch(rowsRemoved(known, updatedAt));
    }
  }

  if (Array.isArray(update.docs)) {
    // Documents are only ever sent for a handful of flights; keep the ones that belong on this screen.
    const relevant = update.docs.filter((doc) => isInRange(doc, range));
    const outOfRange = update.docs.filter((doc) => !isInRange(doc, range) && !!rows[doc.flightId]);
    if (relevant.length || outOfRange.length) liveChanges++;
    if (relevant.length) dispatch(rowsUpserted(relevant, updatedAt));
    // A flight that moved out of the loaded range (date change) no longer belongs on the screen.
    if (outOfRange.length)
      dispatch(
        rowsRemoved(
          outOfRange.map((doc) => doc.flightId),
          updatedAt
        )
      );
    return;
  }

  // The echo of a manual recompute this client just ran: it already reloaded with the result.
  if (lastManualComputedAt !== null && update.computedAt === lastManualComputedAt) return;

  if (update.range) {
    if (rangesOverlap(update.range, range)) dispatch(reloadFlightPairingStatusSoon());
    return;
  }

  if (update.flightIds?.length) {
    // Ids only: we cannot tell which of them fall in our range without asking, so reload (debounced).
    dispatch(reloadFlightPairingStatusSoon());
  }
};

/**
 * Every completed scheduled refresh reports the UTC days it evaluated, changed or not, and rewrites only the rows
 * that changed. Stamp the unchanged rows of those days the way the server's GET reports them, so a window left open
 * does not drift into looking stale between reloads. Those days are also the refresh's current window, which the
 * stale warning judges by: follow it even when it no longer overlaps the range in use (a past month left open while
 * its last day leaves the window at UTC midnight).
 */
const applyVerifiedRange = (
  dispatch: FpsDispatch,
  range: IEpochRange,
  update: IFlightPairingStatusWebsocketUpdate
): void => {
  const verified = update.verifiedRange;
  if (!verified || !Number.isFinite(update.computedAt)) return;
  const keys = dayKeyRange(verified);
  // Only a run over the range in use changes its rows (a load in flight must then fetch again).
  if (rangesOverlap(verified, range)) liveChanges++;
  dispatch(rowsVerified(keys.start, keys.end, update.computedAt, verified));
};

/**
 * Apply a websocket cache update: its changes first (see `applyCacheChanges`), then, whatever those did (including
 * a reload or nothing at all), the days a scheduled refresh verified.
 */
export const applyFlightPairingStatusUpdate =
  (update: IFlightPairingStatusWebsocketUpdate | undefined): FpsThunk =>
  (dispatch: FpsDispatch, getState: () => IRootState): void => {
    const state = getState().flightPairingStatusV2;
    const range = state.requestedRange ?? state.range;
    if (!update || !range || state.openWindows === 0) return;
    applyCacheChanges(dispatch, state.rows, range, update);
    applyVerifiedRange(dispatch, range, update);
  };

/**
 * Force a server-side re-evaluation of the loaded range, then reload it.
 * @returns the server's summary, or null when the request failed
 */
export const recomputeFlightPairingStatus =
  (range: IEpochRange): FpsThunk<Promise<IFlightPairingStatusRecomputeResponse | null>> =>
  async (dispatch: FpsDispatch): Promise<IFlightPairingStatusRecomputeResponse | null> => {
    dispatch(recomputeStarted());
    try {
      const result = await requestFlightPairingStatusRecompute(range, dispatch);
      if (result) {
        lastManualComputedAt = result.computedAt;
        // The range on screen now, which may no longer be the one recomputed if the user moved on meanwhile.
        await dispatch(reloadFlightPairingStatus());
      }
      return result;
    } finally {
      dispatch(recomputeFinished());
    }
  };
